import { doc, getDoc, updateDoc, collection, addDoc, runTransaction } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';

/**
 * Credit Service
 * Handles all credit-related operations: deduction, addition, balance checks, and transaction logging
 */
class CreditService {
  /**
   * Get current credit balance for authenticated user
   * @returns {Promise<number>} Current credit balance
   */
  static async getBalance() {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        return 0;
      }

      const userData = userDoc.data();
      return userData.credits || userData.walletBalance || 0;
    } catch (error) {
      console.error('Error getting credit balance:', error);
      throw error;
    }
  }

  /**
   * Check if user has sufficient credits
   * @param {number} requiredCredits - Credits required for the operation
   * @returns {Promise<{sufficient: boolean, currentBalance: number}>}
   */
  static async checkSufficientCredits(requiredCredits) {
    try {
      const currentBalance = await this.getBalance();
      return {
        sufficient: currentBalance >= requiredCredits,
        currentBalance: currentBalance,
        required: requiredCredits,
        shortfall: Math.max(0, requiredCredits - currentBalance)
      };
    } catch (error) {
      console.error('Error checking sufficient credits:', error);
      throw error;
    }
  }

  /**
   * Deduct credits from user account (atomic transaction)
   * @param {number} amount - Credits to deduct
   * @param {string} description - Description of the deduction
   * @param {Object} metadata - Additional metadata (e.g., projectId, campaignId)
   * @returns {Promise<{success: boolean, newBalance: number}>}
   */
  static async deductCredits(amount, description, metadata = {}) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      if (amount <= 0) {
        throw new Error('Deduction amount must be greater than 0');
      }

      // Ensure amount is treated as float
      const amountFloat = parseFloat(amount);
      const userRef = doc(db, 'users', user.uid);
      
      // Use Firestore transaction for atomic operation
      const result = await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        
        if (!userDoc.exists()) {
          throw new Error('User document not found');
        }

        const userData = userDoc.data();
        // Ensure credits are treated as floats
        const currentCredits = parseFloat(userData.credits || userData.walletBalance || 0);
        
        if (currentCredits < amountFloat) {
          throw new Error(`Insufficient credits. Current: ${currentCredits}, Required: ${amountFloat}`);
        }

        const newBalance = parseFloat((currentCredits - amountFloat).toFixed(2));
        
        // Update credit balance
        transaction.update(userRef, {
          credits: newBalance,
          walletBalance: newBalance, // Keep both for compatibility
          updatedAt: new Date().toISOString()
        });

        return { newBalance, currentCredits };
      });

      // Create transaction record (outside transaction for better performance)
      try {
        await this.logTransaction({
          type: 'deduction',
          amount: -amountFloat, // Negative for deduction
          description,
          metadata,
          status: 'completed'
        });
      } catch (logError) {
        console.error('Error logging transaction (non-critical):', logError);
        // Don't fail the deduction if logging fails
      }

      console.log(`✅ Deducted ${amountFloat} credits. Previous: ${result.currentCredits}, New balance: ${result.newBalance}`);
      return {
        success: true,
        newBalance: result.newBalance,
        previousBalance: result.currentCredits
      };
    } catch (error) {
      console.error('Error deducting credits:', error);
      throw error;
    }
  }

  /**
   * Add credits to user account (atomic transaction with idempotency support)
   * @param {number} amount - Credits to add
   * @param {string} description - Description of the addition
   * @param {Object} metadata - Additional metadata (e.g., stripeSessionId, refundReason, referenceId for idempotency)
   * @returns {Promise<{success: boolean, newBalance: number, wasDuplicate: boolean}>}
   */
  static async addCredits(amount, description, metadata = {}) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      if (amount <= 0) {
        throw new Error('Addition amount must be greater than 0');
      }

      // Idempotency check: if referenceId is provided, check if transaction already exists
      if (metadata.referenceId) {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const transactionsRef = collection(db, 'users', user.uid, 'transactions');
        const existingTransactionQuery = query(
          transactionsRef,
          where('type', '==', 'addition'),
          where('metadata.referenceId', '==', metadata.referenceId)
        );
        const existingTransactions = await getDocs(existingTransactionQuery);
        
        if (!existingTransactions.empty) {
          const existingTransaction = existingTransactions.docs[0].data();
          console.log(`⚠️ Duplicate credit addition detected (referenceId: ${metadata.referenceId}). Skipping.`);
          console.log(`Previous transaction: ${existingTransaction.amount} credits on ${existingTransaction.timestamp}`);
          
          // Return the existing transaction result (get current balance)
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);
          const currentCredits = userDoc.exists() 
            ? parseFloat(userDoc.data().credits || userDoc.data().walletBalance || 0)
            : 0;
          
          return {
            success: true,
            newBalance: currentCredits,
            previousBalance: currentCredits - parseFloat(existingTransaction.amount),
            wasDuplicate: true
          };
        }
      }

      const userRef = doc(db, 'users', user.uid);
      
      // Use Firestore transaction for atomic operation
      const result = await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        
        if (!userDoc.exists()) {
          throw new Error('User document not found');
        }

        const userData = userDoc.data();
        // Ensure credits are treated as floats
        const currentCredits = parseFloat(userData.credits || userData.walletBalance || 0);
        const amountFloat = parseFloat(amount);
        // Round to 2 decimal places to avoid floating point precision issues
        const newBalance = parseFloat((currentCredits + amountFloat).toFixed(2));
        
        // Update credit balance
        transaction.update(userRef, {
          credits: newBalance,
          walletBalance: newBalance, // Keep both for compatibility
          updatedAt: new Date().toISOString()
        });

        return { newBalance, currentCredits };
      });

      // Create transaction record
      try {
        await this.logTransaction({
          type: 'addition',
          amount: amount,
          description,
          metadata,
          status: 'completed'
        });
      } catch (logError) {
        console.error('Error logging transaction (non-critical):', logError);
        // Don't fail the addition if logging fails
      }

      console.log(`✅ Added ${amount} credits. New balance: ${result.newBalance}`);
      return {
        success: true,
        newBalance: result.newBalance,
        previousBalance: result.currentCredits,
        wasDuplicate: false
      };
    } catch (error) {
      console.error('Error adding credits:', error);
      throw error;
    }
  }

  /**
   * Log a transaction to Firestore
   * @param {Object} transactionData - Transaction data
   * @returns {Promise<string>} Transaction document ID
   */
  static async logTransaction(transactionData) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const transactionsRef = collection(db, 'users', user.uid, 'transactions');
      
      const transactionDoc = {
        ...transactionData,
        timestamp: new Date().toISOString(),
        createdAt: new Date().toISOString()
      };

      const docRef = await addDoc(transactionsRef, transactionDoc);
      return docRef.id;
    } catch (error) {
      console.error('Error logging transaction:', error);
      throw error;
    }
  }

  /**
   * Get transaction history for user
   * @param {number} limit - Maximum number of transactions to return
   * @returns {Promise<Array>} Array of transaction documents
   */
  static async getTransactionHistory(limit = 50) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const transactionsRef = collection(db, 'users', user.uid, 'transactions');
      const { getDocs, query, orderBy, limit: limitQuery } = await import('firebase/firestore');
      
      const q = query(transactionsRef, orderBy('timestamp', 'desc'), limitQuery(limit));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting transaction history:', error);
      throw error;
    }
  }
}

export default CreditService;

